import { Css, useTestIds } from "@homebound/beam";
import { emptyCellDash } from "src/components/gridTableCells";
import { Maybe } from "src/generated/graphqlTypes";
import { centsToDollars, currencyAbbreviation, formatNumberToString, isDefined } from "src/utils";
import { Icon } from "./Icon";

export type PriceProps = {
  id?: string;
  valueInCents: Maybe<number>;
  dropZero?: boolean;
  /** Prefixes a plus for positive numbers, like +$1,000. Negatives will always display */
  displayDirection?: boolean;
  /** Makes positive numbers red (e.g. price increase) and negative colors green */
  invertColors?: boolean;
  /** trims trailing zeros after decimal point */
  trim?: boolean;
  /** Pairs with displayDirection + invertColors to determine how to display Zero. Neutral overrides either to display without color. */
  zeroIs?: "positive" | "negative" | "neutral";
  /** Value to compare against the main amount, used to display a difference if present */
  comparedValueInCents?: Maybe<number>;
  /** Makes positive numbers red (e.g. price increase) and negative colors green for the compared value difference */
  invertComparedValueColors?: boolean;
  maxDecimalPlaces?: number;
  minDecimalPlaces?: number;
  /** Makes the field green if above, red if below */
  desiredValueInCents?: Maybe<number>;
};

export function Price(props: PriceProps) {
  const {
    id,
    valueInCents,
    zeroIs = "positive", // This is less a default than an expected byproduct of !!valueInCents
    comparedValueInCents,
    invertComparedValueColors = false,
  } = props;
  const rootId = useTestIds({}, id || "price");
  const colorStyle = getColorStyle(props);
  const formattedPrice = formatToPrice(props);
  const comparedValue = comparedValueInCents && valueInCents ? valueInCents - comparedValueInCents : undefined;
  const isComparedValueNegative = isNegative(comparedValue, zeroIs) !== invertComparedValueColors;

  return (
    <>
      <span {...rootId} css={colorStyle}>
        {formattedPrice}
      </span>
      {comparedValue && (
        <span
          {...rootId.comparedValue}
          css={Css.df.xsSb.gapPx(3).fdr.ml1.if(isComparedValueNegative).red500.else.green500.$}
        >
          {currencyAbbreviation(comparedValue, 1)}
          <div css={Css.mya.$}>
            <Icon icon={isNegative(comparedValue, zeroIs) ? "arrowDown" : "arrowUp"} inc={1.8} />
          </div>
        </span>
      )}
    </>
  );
}

function getColorStyle({
  valueInCents,
  displayDirection = false,
  invertColors = false,
  zeroIs = "positive", // This is less a default than an expected byproduct of !!valueInCents
  desiredValueInCents = undefined,
}: PriceProps) {
  if (valueInCents === 0 && zeroIs === "neutral") return Css.$;

  const isNegativeValue = isNegative(valueInCents, zeroIs);
  let green = displayDirection && !isNegativeValue ? true : isNegativeValue ? false : undefined;
  // Green if above desired value, red if below
  if (desiredValueInCents && isDefined(valueInCents)) {
    green = isNegativeValue ? valueInCents <= desiredValueInCents : valueInCents >= desiredValueInCents;
  }
  if (green === undefined) return;
  if (invertColors) green = !green;
  return green ? Css.green500.$ : Css.red600.$;
}

function isNegative(valueInCents: Maybe<number>, zeroIs = "positive") {
  let isNegative = !!valueInCents && valueInCents < 0;
  const isZero = valueInCents === 0;
  if (isZero && zeroIs === "negative") isNegative = !isNegative;
  return isNegative;
}

export function formatToPrice(props: PriceProps): string {
  const {
    valueInCents,
    dropZero = false,
    trim = false,
    displayDirection = false,
    zeroIs = "positive",
    maxDecimalPlaces,
    minDecimalPlaces,
  } = props;
  const isNegativeValue = isNegative(valueInCents, zeroIs);
  const prefix = isNegativeValue ? "-$" : displayDirection ? "+$" : "$";
  return typeof valueInCents !== "number" || (dropZero && valueInCents === 0)
    ? emptyCellDash
    : `${prefix}${formatNumberToString(centsToDollars(Math.abs(valueInCents)), trim, maxDecimalPlaces, minDecimalPlaces)}`;
}
